/* import __COLOCATED_TEMPLATE__ from './front.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type HandoffToExternalTicket from 'embercom/models/operator/visual-builder/step/handoff-to-external-ticket';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import { type ConnectionOption } from 'embercom/components/standalone/common/connect-card';
import { request } from 'embercom/lib/inbox/requests';

interface FrontHandoffData {
  selectedInboxId?: string;
  selectedTeammateId?: string;
}

export interface Args {
  step: HandoffToExternalTicket;
  readOnly?: boolean;
}

export default class FrontExternalTicketHandoffStepComponent extends Component<Args> {
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare appService: $TSFixMe;
  @service declare intercomConfirmService: IntercomConfirmService;

  @tracked apiKey = '';

  @tracked isConnected = false;
  @tracked isLoading = false;
  @tracked isConnecting = false;

  @tracked showApiKeyModal = false;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.initializeHandoffData();
    this.checkConnectionStatus();
  }

  private get handoffData(): FrontHandoffData {
    return this.args.step.handoffData as FrontHandoffData;
  }

  private initializeHandoffData() {
    let step = this.args.step as any;

    if (!step.handoffData) {
      step.handoffData = {};
    }

    if (!step.handoffData.selectedInboxId) {
      step.handoffData.selectedInboxId = '';
    }

    if (!step.handoffData.selectedTeammateId) {
      step.handoffData.selectedTeammateId = '';
    }
  }

  private async checkConnectionStatus() {
    this.isLoading = true;

    try {
      await this.makeStatusRequest();
      this.isConnected = true;
    } catch (error) {
      this.isConnected = false;
    } finally {
      this.isLoading = false;
    }
  }

  private async makeStatusRequest() {
    return await request(`/standalone/front/connection_status?app_id=${this.appService.app.id}`, {
      method: 'GET',
    });
  }

  private async makeConnectRequest(apiKey: string) {
    return await request('/standalone/front/connect', {
      method: 'POST',
      body: JSON.stringify({
        app_id: this.appService.app.id,
        api_key: apiKey,
      }),
    });
  }

  private async makeRevokeRequest() {
    return await request('/standalone/front/revoke', {
      method: 'POST',
      body: JSON.stringify({
        app_id: this.appService.app.id,
      }),
    });
  }

  get isLoadingOrConnecting() {
    return this.isLoading;
  }

  get stepTitle() {
    return this.intl.t('operator.workflows.visual-builder.create-external-ticket.title', {
      integration: 'Front', // eslint-disable-line @intercom/intercom/no-bare-strings
      htmlSafe: true,
    });
  }

  get connectionCardOptions(): ConnectionOption {
    return {
      value: 'front',
      icon: 'ticket',
      label: this.intl.t('standalone.external-ticket-handoff.front.connect-card-title'),
      description: this.intl.t('standalone.external-ticket-handoff.front.connect-card-description'),
      isConnected: this.isConnected,
      isAuthFailed: false, // todo; implement endpoint for getting this
      stampConnected: this.intl.t('standalone.external-ticket-handoff.front.connected'),
      stampNotConnected: this.intl.t('standalone.external-ticket-handoff.front.not-connected'),
      buttonConnected: this.intl.t('standalone.external-ticket-handoff.front.revoke-button'),
      buttonNotConnected: this.intl.t('standalone.external-ticket-handoff.front.connect-button'),
      manage: () => this.handleRevoke(),
      connect: () => this.openApiKeyModal(),
    };
  }

  @dropTask
  *connectToFront() {
    if (!this.apiKey.trim()) {
      this.notificationsService.notifyError(
        this.intl.t('standalone.external-ticket-handoff.front.api-key-required'),
      );
      return;
    }

    this.isLoading = true;

    try {
      yield this.makeConnectRequest(this.apiKey);
      this.handleConnectionSuccess();
    } catch (error) {
      this.handleConnectionError(error);
    } finally {
      this.isLoading = false;
    }
  }

  private handleConnectionSuccess() {
    this.isConnected = true;
    this.showApiKeyModal = false;

    this.notificationsService.notifyConfirmation(
      this.intl.t('standalone.external-ticket-handoff.front.connection-successful'),
    );
  }

  private handleConnectionError(error: any) {
    console.error('Front connection failed:', error);
    this.notificationsService.notifyError(
      this.intl.t('standalone.external-ticket-handoff.front.connection-failed'),
    );
  }

  @action
  handleConnect() {
    taskFor(this.connectToFront).perform();
  }

  @action
  handleApiKeyInput(apiKey: string) {
    this.apiKey = apiKey.trim();
  }

  @action
  handleInboxChange(inboxId: string) {
    this.handoffData.selectedInboxId = inboxId;
    this.args.step.handoffData = { ...this.handoffData };
  }

  @action
  handleTeammateChange(teammateId: string) {
    this.handoffData.selectedTeammateId = teammateId;
    this.args.step.handoffData = { ...this.handoffData };
  }

  @action
  async handleRevoke() {
    let confirmed = await this.intercomConfirmService.confirm({
      title: this.intl.t('standalone.external-ticket-handoff.front.revoke-connection-title'),
      body: this.intl.t('standalone.external-ticket-handoff.front.revoke-connection-body'),
      confirmButtonText: this.intl.t('standalone.external-ticket-handoff.front.revoke-button'),
      cancelButtonText: this.intl.t('standalone.external-ticket-handoff.front.cancel-button'),
    });

    if (!confirmed) {
      return;
    }

    try {
      await this.makeRevokeRequest();
      this.handleRevokeSuccess();
    } catch (error) {
      this.handleRevokeError(error);
    }
  }

  private handleRevokeSuccess() {
    this.isConnected = false;

    this.handoffData.selectedInboxId = undefined;
    this.handoffData.selectedTeammateId = undefined;
    this.args.step.handoffData = { ...this.handoffData };

    this.notificationsService.notifyConfirmation(
      this.intl.t('standalone.external-ticket-handoff.front.revoke-successful'),
    );
  }

  private handleRevokeError(error: any) {
    console.error('Front revoke failed:', error);
    this.notificationsService.notifyError(
      this.intl.t('standalone.external-ticket-handoff.front.revoke-failed'),
    );
  }

  @action
  openApiKeyModal() {
    this.showApiKeyModal = true;
  }

  @action
  closeApiKeyModal() {
    this.showApiKeyModal = false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::ExternalTicketHandoffSteps::Front': typeof FrontExternalTicketHandoffStepComponent;
  }
}
